import { createContext, ReactNode, useContext, useRef, useState, useEffect } from 'react';
import Splash from '../../component/Splash';
import { useQlubRouter } from '../../hook/router';
import { getSplashConfig, ISplashConfig } from '../../component/Splash/utils';
import SplashLegacy from '../../component/SplashLegacy';
import { IShowProps, ISplashContext } from './types';
import { onPushDimensionEvent } from '../../lib/gtm';
import { themeOptions } from '../config';

const SplashContext = createContext<ISplashContext>({
    show: () => {
        console.log('Not implemented ');
    },
    hide: () => {
        console.log('Not implemented');
    },
});

interface Props {
    children?: ReactNode;
}

export function SplashContextProvider({ children }: Props) {
    const { lang, url } = useQlubRouter();

    const [legacy, setLegacy] = useState(false);
    const [config, setConfig] = useState<ISplashConfig | null>(null);
    const [smoothFinish, setSmoothFinish] = useState(false);

    const timeRef = useRef<number>(0);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const langRef = useRef(lang);

    langRef.current = lang;

    useEffect(() => {
        if (url.cc) {
            onPushDimensionEvent({
                cc: url.cc,
            });
        }
    }, []);

    const show = ({ countryConfig, vendor, legacy: _legacy }: IShowProps) => {
        if (legacy !== _legacy) {
            setLegacy(_legacy || false);
        }
        if (!timeRef.current) {
            timeRef.current = Date.now();
        }
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            setConfig(getSplashConfig({ countryConfig, vendor, lang: langRef.current }));
        }, 100);
    };

    const hide = (smoothFinishArg?: boolean) => {
        if (!config) {
            timeRef.current = 0;
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            return;
        }
        const now = Date.now();
        if (smoothFinishArg && now - timeRef.current > 500) {
            setSmoothFinish(true);

            setTimeout(() => {
                setConfig(null);
            }, 2000);
        } else {
            setConfig(null);
        }
        timeRef.current = 0;
    };

    return (
        <SplashContext.Provider value={{ show, hide }}>
            {config &&
                (legacy ? (
                    <SplashLegacy hideIcon={themeOptions.hideLogo} />
                ) : (
                    <Splash config={config} smoothFinish={smoothFinish} />
                ))}
            {children}
        </SplashContext.Provider>
    );
}

export const useSplash = () => useContext(SplashContext);
